// Define log levels
export enum LogLevel {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  DEBUG = 'DEBUG'
}

// Define log entry structure
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
}

/**
 * Creates a timestamped log ID
 * @param prefix - Optional prefix for the log ID
 * @returns A string in the format prefix-YYYY-MM-DDTHH-MM-SS
 */
export function createTimestampedId(prefix: string = 'app'): string {
  const now = new Date();
  const timestamp = now.toISOString()
    .replace(/:/g, '-')
    .replace(/\..+/, '');
  
  return `${prefix}-${timestamp}`;
}

/**
 * Format a log entry as a string
 * @param entry - The log entry to format
 * @returns Formatted log string
 */
function formatLogEntry(entry: LogEntry): string {
  let formattedEntry = `[${entry.timestamp}] [${entry.level}] [${entry.category}] ${entry.message}`;
  
  if (entry.data) {
    try {
      const dataStr = JSON.stringify(entry.data, null, 2);
      formattedEntry += `\nData: ${dataStr}`;
    } catch (error) {
      formattedEntry += `\nData: [Circular or unserializable data]`;
    }
  }
  
  return formattedEntry;
}

/**
 * Stores a log entry in localStorage with a maximum of 100 entries
 * @param logId - The log ID to store under
 * @param entry - The log entry to store
 */
function storeLogEntry(logId: string, entry: LogEntry): void {
  try {
    // Get existing log entries or create a new array
    const existingLogsStr = localStorage.getItem(logId);
    let existingLogs: LogEntry[] = [];
    
    if (existingLogsStr) {
      existingLogs = JSON.parse(existingLogsStr);
    }
    
    // Add the new entry
    existingLogs.push(entry);
    
    // Limit to 100 entries (to prevent localStorage size issues)
    if (existingLogs.length > 100) {
      existingLogs = existingLogs.slice(-100);
    }
    
    // Store back in localStorage
    localStorage.setItem(logId, JSON.stringify(existingLogs));
    
    // Also log to console for immediate visibility
    console.log(formatLogEntry(entry));
  } catch (error) {
    console.error('Failed to store log entry:', error);
  }
}

/**
 * Creates a logger instance that stores logs in localStorage
 * @param logId - ID to store logs under in localStorage
 * @param defaultCategory - Default category for log entries
 * @returns A logger object with methods for different log levels
 */
export function createLogger(logId: string, defaultCategory: string = 'app') {
  // Initialize the log with a header
  const existingLogsStr = localStorage.getItem(logId);
  if (!existingLogsStr) {
    localStorage.setItem(logId, JSON.stringify([]));
  }
  
  return {
    /**
     * Logs an informational message
     * @param message - The message to log
     * @param data - Optional data to include in the log
     * @param category - Optional category (overrides default)
     */
    info(message: string, data?: any, category?: string): void {
      storeLogEntry(logId, {
        timestamp: new Date().toISOString(),
        level: LogLevel.INFO,
        category: category || defaultCategory,
        message,
        data
      });
    },
    
    /**
     * Logs a warning message
     * @param message - The message to log
     * @param data - Optional data to include in the log
     * @param category - Optional category (overrides default)
     */
    warning(message: string, data?: any, category?: string): void {
      storeLogEntry(logId, {
        timestamp: new Date().toISOString(),
        level: LogLevel.WARNING,
        category: category || defaultCategory,
        message,
        data
      });
    },
    
    /**
     * Logs an error message
     * @param message - The message to log
     * @param error - The error object
     * @param additionalData - Optional additional data
     * @param category - Optional category (overrides default)
     */
    error(message: string, error: any, additionalData?: any, category?: string): void {
      const errorData = {
        message: error?.message || String(error),
        stack: error?.stack,
        ...additionalData
      };
      
      storeLogEntry(logId, {
        timestamp: new Date().toISOString(),
        level: LogLevel.ERROR,
        category: category || defaultCategory,
        message,
        data: errorData
      });
    },
    
    /**
     * Logs a debug message
     * @param message - The message to log
     * @param data - Optional data to include in the log
     * @param category - Optional category (overrides default)
     */
    debug(message: string, data?: any, category?: string): void {
      storeLogEntry(logId, {
        timestamp: new Date().toISOString(),
        level: LogLevel.DEBUG,
        category: category || defaultCategory,
        message,
        data
      });
    },
    
    /**
     * Gets the log ID
     * @returns The log ID
     */
    getLogId(): string {
      return logId;
    },
    
    /**
     * Gets all log entries
     * @returns Array of log entries
     */
    getAllLogs(): LogEntry[] {
      const logsStr = localStorage.getItem(logId);
      return logsStr ? JSON.parse(logsStr) : [];
    },
    
    /**
     * Clears all logs for this log ID
     */
    clearLogs(): void {
      localStorage.removeItem(logId);
      localStorage.setItem(logId, JSON.stringify([]));
    }
  };
}

/**
 * Context map for highlight generation logs
 */
export type HighlightLogContext = 'admin' | 'generate';

/**
 * Get or create a logger specifically for highlight generation with context awareness
 * @param context - The context of the logging (admin or generate page)
 * @returns A configured logger for highlight generation
 */
export function getHighlightLogger(context: HighlightLogContext) {
  // Generate a unique ID for this highlight generation session if it doesn't exist yet
  const storageKey = `highlight-logger-${context}-id`;
  let loggerId = localStorage.getItem(storageKey);
  
  if (!loggerId) {
    loggerId = createTimestampedId(`highlight-${context}`);
    localStorage.setItem(storageKey, loggerId);
  }
  
  return createLogger(loggerId, `highlight-${context}`);
}

/**
 * Get logs for a specific highlight generation context as a downloadable string
 * @param context - The context to get logs for (admin or generate)
 * @returns Formatted log string or empty string if no logs found
 */
export function getHighlightLogsAsString(context: HighlightLogContext): string {
  const storageKey = `highlight-logger-${context}-id`;
  const loggerId = localStorage.getItem(storageKey);
  
  if (!loggerId) return "";
  
  const logsStr = localStorage.getItem(loggerId);
  if (!logsStr) return "";
  
  const logs = JSON.parse(logsStr);
  
  return logs
    .map((entry: LogEntry) => {
      let formatted = `[${entry.timestamp}] [${entry.level}] ${entry.message}`;
      if (entry.data) {
        formatted += `\nData: ${JSON.stringify(entry.data, null, 2)}`;
      }
      return formatted;
    })
    .join('\n\n');
}

/**
 * Download highlight logs as a text file
 * @param context - The context to download logs for
 */
export function downloadHighlightLogs(context: HighlightLogContext): void {
  const logs = getHighlightLogsAsString(context);
  if (!logs) return;
  
  const blob = new Blob([logs], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `highlight-logs-${context}-${new Date().toISOString().replace(/:/g, '-')}.txt`;
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
} 